import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAdminTodos,
  createAdminTodo,
  bootstrapAdminTodos,
  completeAdminTodo,
  reopenAdminTodo,
  deleteAdminTodo,
} from '../services/adminTodoService';
import './AdminYapilacaklarPage.css';
import { useConfirmDialog } from '../contexts/ConfirmContext';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 401) return 'Oturum süresi dolmuş olabilir; tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu sayfa yalnızca yöneticiler içindir.';
  return (
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    fallback
  );
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const AdminYapilacaklarPage = () => {
  const { confirm } = useConfirmDialog();
  const [aktif, setAktif] = useState([]);
  const [bitenler, setBitenler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [yeniBaslik, setYeniBaslik] = useState('');
  const [eklemeYukleniyor, setEklemeYukleniyor] = useState(false);
  const [bootstrapYukleniyor, setBootstrapYukleniyor] = useState(false);

  const [modalAcik, setModalAcik] = useState(false);
  const [secilen, setSecilen] = useState(null);
  const [tamamlamaNotu, setTamamlamaNotu] = useState('');
  const [tamamlamaYukleniyor, setTamamlamaYukleniyor] = useState(false);

  const yukle = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchAdminTodos();
      const veri = res?.veri || {};
      setAktif(Array.isArray(veri.aktif) ? veri.aktif : []);
      setBitenler(Array.isArray(veri.bitenler) ? veri.bitenler : []);
    } catch (err) {
      setError(getErrorMessage(err, 'Liste yüklenemedi'));
      setAktif([]);
      setBitenler([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    yukle();
  }, [yukle]);

  const handleEkle = async (e) => {
    e.preventDefault();
    const t = yeniBaslik.trim();
    if (!t) return;
    setEklemeYukleniyor(true);
    try {
      await createAdminTodo(t);
      setYeniBaslik('');
      if (window.showNotification) window.showNotification('success', 'Yapılacak eklendi');
      await yukle();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Eklenemedi'));
      }
    } finally {
      setEklemeYukleniyor(false);
    }
  };

  const handleBootstrap = async () => {
    setBootstrapYukleniyor(true);
    try {
      const res = await bootstrapAdminTodos();
      const ek = res?.veri?.eklenen ?? 0;
      if (ek > 0) {
        if (window.showNotification) window.showNotification('success', `${ek} örnek madde eklendi`);
      } else if (window.showNotification) {
        window.showNotification('info', res?.mesaj || 'Liste zaten dolu');
      }
      await yukle();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Yüklenemedi'));
      }
    } finally {
      setBootstrapYukleniyor(false);
    }
  };

  const tamamlaModalAc = (todo) => {
    setSecilen(todo);
    setTamamlamaNotu('');
    setModalAcik(true);
  };

  const tamamlaModalKapat = () => {
    setModalAcik(false);
    setSecilen(null);
    setTamamlamaNotu('');
  };

  const handleTamamlaOnay = async () => {
    if (!secilen?.id) return;
    setTamamlamaYukleniyor(true);
    try {
      await completeAdminTodo(secilen.id, tamamlamaNotu.trim());
      if (window.showNotification) window.showNotification('success', 'Tamamlandı');
      setModalAcik(false);
      setSecilen(null);
      setTamamlamaNotu('');
      await yukle();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'İşlem başarısız'));
      }
    } finally {
      setTamamlamaYukleniyor(false);
    }
  };

  const handleYenidenAc = async (id) => {
    const ok = await confirm({
      title: 'Maddeyi yeniden aç',
      message: 'Bu maddeyi yeniden açık listesine taşımak istiyor musunuz?',
      variant: 'warning',
      confirmLabel: 'Evet',
    });
    if (!ok) return;
    try {
      await reopenAdminTodo(id);
      if (window.showNotification) window.showNotification('success', 'Yeniden açıldı');
      await yukle();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'İşlem başarısız'));
      }
    }
  };

  const handleSil = async (id) => {
    const ok = await confirm({
      title: 'Kalıcı sil',
      message: 'Bu maddeyi kalıcı olarak silmek istiyor musunuz?',
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      await deleteAdminTodo(id);
      if (window.showNotification) window.showNotification('success', 'Silindi');
      await yukle();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Silinemedi'));
      }
    }
  };

  const bosListe = aktif.length === 0 && bitenler.length === 0;

  return (
    <div className="page-container admin-todo-wrap">
      <div className="page-header">
        <h1>Yapılacaklar</h1>
      </div>

      {error && (
        <div className="admin-todo-alert admin-todo-alert--err" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '28px', display: 'block', marginBottom: '12px' }} />
          Yükleniyor...
        </div>
      ) : (
        <div className="admin-todo-grid">
          <section className="admin-todo-panel" aria-labelledby="admin-todo-aktif-baslik">
            <div className="admin-todo-panel-head">
              <h2 id="admin-todo-aktif-baslik">
                <i className="fas fa-circle-notch" aria-hidden />
                Açık yapılacaklar
              </h2>
              <span className="admin-todo-count">{aktif.length}</span>
            </div>

            <form className="admin-todo-add" onSubmit={handleEkle}>
              <div className="admin-todo-add-row">
                <input
                  type="text"
                  className="admin-todo-input"
                  placeholder="Yeni madde yazın…"
                  value={yeniBaslik}
                  onChange={(e) => setYeniBaslik(e.target.value)}
                  maxLength={2000}
                  aria-label="Yeni yapılacak"
                />
                <button
                  type="submit"
                  className="admin-todo-btn admin-todo-btn--primary"
                  disabled={eklemeYukleniyor || !yeniBaslik.trim()}
                >
                  {eklemeYukleniyor ? (
                    <i className="fas fa-spinner fa-spin" />
                  ) : (
                    <i className="fas fa-plus" />
                  )}
                  Ekle
                </button>
              </div>
              {bosListe && (
                <div className="admin-todo-bootstrap-hint">
                  İlk kurulum: düzenlenmiş gereksinim listesini tek tıkla ekleyebilirsiniz (veritabanı boşken). İstediğinizi sonra
                  silebilir veya düzenleyebilirsiniz.
                  <div style={{ marginTop: '12px' }}>
                    <button
                      type="button"
                      className="admin-todo-btn admin-todo-btn--ghost admin-todo-btn--sm"
                      onClick={handleBootstrap}
                      disabled={bootstrapYukleniyor}
                    >
                      {bootstrapYukleniyor ? (
                        <i className="fas fa-spinner fa-spin" />
                      ) : (
                        <i className="fas fa-magic" />
                      )}
                      Başlangıç listesini yükle
                    </button>
                  </div>
                </div>
              )}
            </form>

            {aktif.length === 0 ? (
              <div className="admin-todo-empty">
                <i className="fas fa-clipboard-check" aria-hidden />
                Açık madde yok. Yukarıdan yeni ekleyin veya başlangıç listesini yükleyin.
              </div>
            ) : (
              <ul className="admin-todo-list">
                {aktif.map((t) => (
                  <li key={t.id} className="admin-todo-item">
                    <div className="admin-todo-item-mark" aria-hidden>
                      <i className="fas fa-tasks" />
                    </div>
                    <div className="admin-todo-item-body">
                      <p className="admin-todo-item-title">{t.baslik}</p>
                      <div className="admin-todo-item-meta">Oluşturulma: {formatDateTime(t.olusturmaTarihi)}</div>
                    </div>
                    <div className="admin-todo-item-actions">
                      <button
                        type="button"
                        className="admin-todo-btn admin-todo-btn--success admin-todo-btn--sm"
                        onClick={() => tamamlaModalAc(t)}
                      >
                        <i className="fas fa-check" />
                        Tamamla
                      </button>
                      <button
                        type="button"
                        className="admin-todo-btn admin-todo-btn--danger admin-todo-btn--sm"
                        onClick={() => handleSil(t.id)}
                      >
                        Sil
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="admin-todo-panel" aria-labelledby="admin-todo-biten-baslik">
            <div className="admin-todo-panel-head">
              <h2 id="admin-todo-biten-baslik">
                <i className="fas fa-check-double" aria-hidden />
                Bitenler
              </h2>
              <span className="admin-todo-count admin-todo-count--done">{bitenler.length}</span>
            </div>

            {bitenler.length === 0 ? (
              <div className="admin-todo-empty">
                <i className="fas fa-history" aria-hidden />
                Henüz tamamlanan madde yok.
              </div>
            ) : (
              <ul className="admin-todo-list">
                {bitenler.map((t) => (
                  <li key={t.id} className="admin-todo-item">
                    <div
                      className="admin-todo-item-mark"
                      style={{
                        background: 'linear-gradient(145deg, #ecfdf5, #d1fae5)',
                        color: '#047857',
                      }}
                      aria-hidden
                    >
                      <i className="fas fa-check" />
                    </div>
                    <div className="admin-todo-item-body">
                      <p className="admin-todo-item-title">{t.baslik}</p>
                      <div className="admin-todo-item-meta">
                        Tamamlanma: {formatDateTime(t.tamamlanmaTarihi)}
                        {t.tamamlayan?.ad || t.tamamlayan?.kullaniciAdi
                          ? ` · ${t.tamamlayan.ad || t.tamamlayan.kullaniciAdi}`
                          : ''}
                      </div>
                      {t.tamamlanmaNotu ? (
                        <div className="admin-todo-done-note">
                          <strong>Tamamlanma notu</strong>
                          {t.tamamlanmaNotu}
                        </div>
                      ) : null}
                    </div>
                    <div className="admin-todo-item-actions">
                      <button
                        type="button"
                        className="admin-todo-btn admin-todo-btn--ghost admin-todo-btn--sm"
                        onClick={() => handleYenidenAc(t.id)}
                      >
                        <i className="fas fa-undo" />
                        Yeniden aç
                      </button>
                      <button
                        type="button"
                        className="admin-todo-btn admin-todo-btn--danger admin-todo-btn--sm"
                        onClick={() => handleSil(t.id)}
                      >
                        Sil
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {modalAcik && secilen && (
        <div
          className="admin-todo-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-todo-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !tamamlamaYukleniyor) tamamlaModalKapat();
          }}
        >
          <div className="admin-todo-modal">
            <div className="admin-todo-modal-head">
              <h3 id="admin-todo-modal-title">Maddeyi tamamla</h3>
              <p>{secilen.baslik}</p>
            </div>
            <div className="admin-todo-modal-body">
              <label htmlFor="admin-todo-tamamlama-notu">Tamamlanma notu (isteğe bağlı)</label>
              <textarea
                id="admin-todo-tamamlama-notu"
                className="admin-todo-textarea"
                value={tamamlamaNotu}
                onChange={(e) => setTamamlamaNotu(e.target.value)}
                maxLength={5000}
                placeholder="Ne yaptınız, hangi ortamda test edildi, kısa özet…"
              />
            </div>
            <div className="admin-todo-modal-foot">
              <button
                type="button"
                className="admin-todo-btn admin-todo-btn--ghost"
                onClick={tamamlaModalKapat}
                disabled={tamamlamaYukleniyor}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="admin-todo-btn admin-todo-btn--success"
                onClick={handleTamamlaOnay}
                disabled={tamamlamaYukleniyor}
              >
                {tamamlamaYukleniyor ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                Tamamlandı olarak kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminYapilacaklarPage;
